<template>
  <div class="om-search">
    <label class="om-eyebrow" for="om-search-input">{{ copy.searchLabel }}</label>
    <div class="om-search-field">
      <i class="ni ni-zoom-split-in om-search-icon" aria-hidden="true"></i>
      <input
        id="om-search-input"
        v-model="query"
        class="om-search-input"
        type="search"
        :placeholder="copy.searchPlaceholder"
        autocomplete="off"
        @input="scheduleSearch"
      >
    </div>
    <p v-if="query && !isLongEnough" class="om-search-hint">{{ copy.searchHint }}</p>
    <p v-else-if="isSearching" class="om-search-hint">{{ copy.searching }}</p>
    <p v-else-if="hasSearched && results.length === 0" class="om-search-hint">{{ copy.searchEmpty }}</p>
    <ul v-if="isLongEnough && results.length" class="om-results">
      <li v-for="result in results" :key="result.slug">
        <router-link class="om-result" :to="resultRoute(result)">
          <span class="om-result-part">{{ result.part_title }}</span>
          <span class="om-result-title">{{ result.title }} <manual-change-badge :status="result.change_status" /></span>
          <span v-if="result.snippet" class="om-result-snippet">{{ result.snippet }}</span>
        </router-link>
      </li>
    </ul>
  </div>
</template>

<script>
import ManualChangeBadge from './ManualChangeBadge.vue';
import { MANUAL_COPY, MANUAL_ROUTES } from './manualCopy';

const MINIMUM_QUERY_LENGTH = 3;
const SEARCH_DELAY_MS = 300;

export default {
  name: 'ManualSearchBox',
  components: { ManualChangeBadge },
  props: {
    manualKey: { type: String, required: true },
  },
  data() {
    return { copy: MANUAL_COPY, query: '', results: [], isSearching: false, hasSearched: false, timer: null, requestId: 0 };
  },
  computed: {
    isLongEnough() {
      return this.query.trim().length >= MINIMUM_QUERY_LENGTH;
    },
  },
  beforeDestroy() {
    clearTimeout(this.timer);
  },
  methods: {
    scheduleSearch() {
      clearTimeout(this.timer);
      this.hasSearched = false;
      if (!this.isLongEnough) {
        this.results = [];
        return;
      }
      this.timer = setTimeout(this.runSearch, SEARCH_DELAY_MS);
    },
    async runSearch() {
      const requestId = ++this.requestId;
      this.isSearching = true;
      try {
        const results = await this.$store.dispatch('operationsManual/search', { manualKey: this.manualKey, query: this.query.trim() });
        if (requestId === this.requestId) this.results = results;
      } catch (error) {
        if (requestId === this.requestId) this.results = [];
      } finally {
        if (requestId === this.requestId) {
          this.isSearching = false;
          this.hasSearched = true;
        }
      }
    },
    resultRoute(result) {
      return { name: MANUAL_ROUTES.chapter, params: { manualKey: this.manualKey, chapterSlug: result.slug } };
    },
  },
};
</script>

<style scoped>
.om-search-field { position: relative; }
.om-search-icon { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: var(--om-faint); }
.om-search-input { width: 100%; box-sizing: border-box; min-height: 46px; background: var(--om-soft); border: 1.5px solid var(--om-line); color: var(--om-ink); font: inherit; padding: 10px 14px 10px 40px; border-radius: 10px; }
.om-search-input:focus { outline: none; border-color: var(--om-green); background: var(--om-surface); }
.om-search-hint { margin: 8px 0 0; font-size: 13px; color: var(--om-muted); }
.om-results { list-style: none; padding: 0; margin: 10px 0 0; display: grid; gap: 8px; }
.om-result { display: grid; gap: 3px; padding: 12px 14px; border: 1px solid var(--om-line); border-radius: 10px; color: var(--om-ink); text-decoration: none; background: var(--om-surface); }
.om-result:hover { border-color: var(--om-green); color: var(--om-ink); }
.om-result-part { font-size: 11px; color: var(--om-faint); text-transform: uppercase; letter-spacing: .05em; }
.om-result-title { font-weight: 650; display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.om-result-snippet { font-size: 13px; color: var(--om-muted); line-height: 1.5; }
</style>
